/**
 * The checkpoint rail's data: the project's restore points, and what you can do
 * with them: save one, go back to one, drop one.
 *
 * The timeline is the checkpoint log, not the conversation. A checkpoint is a
 * project state somebody thought worth naming (the agent after finishing a
 * piece of work, or a member pressing the button), so the rail reads as "states
 * this project has been in", and its labels are the checkpoint's own
 * description rather than whatever was typed in chat.
 *
 * It is a tree, not a list. Going back to an older checkpoint and saving again
 * branches from there, and the log's `head` says which state the workspace is
 * currently on, which is the one node the rail draws bright. The shape of that
 * is `checkpoint-graph.ts`; this hook is the Automerge half.
 *
 * Restoring is files-only. Nothing is removed from the transcript: a checkpoint
 * isn't a message boundary (agents take them mid-turn), so there is no honest
 * place to cut. The restore shows up in chat as its own marker instead.
 */
import type { AutomergeUrl, Repo } from "@automerge/automerge-repo";
import { useDocument } from "@automerge/react";
import {
  captureAndCreateCheckpoint,
  deleteCheckpoint,
  revertToCheckpoint,
} from "playground/checkpoints";
import type { PlayFileInode } from "playground/fs/types";
import type { CheckpointsRefDoc } from "playground/project";
import { useCallback, useMemo, useState } from "react";
import { invariant } from "ts-invariant";

import {
  type CheckpointGraph,
  railGraph,
  type RailCheckpoint,
} from "./checkpoint-graph";

export type { RailCheckpoint } from "./checkpoint-graph";

export interface CheckpointRailOptions {
  repo: Repo | null;
  /** The project index doc: what a restore writes through. */
  indexUrl: AutomergeUrl;
  /** Minted single-writer at project load; absent for a beat on a cold start. */
  checkpointsDocUrl: AutomergeUrl | undefined;
  /** The workspace as it stands now, snapshotted on capture. */
  files: Record<string, PlayFileInode>;
  /**
   * The change message stamped on every doc a restore rewrites. Hosts already
   * differ here. Hearth encodes `{author, restore}` for its attribution
   * projection, Bear writes a plain label, so the wording is theirs.
   */
  restoreMessage: (checkpoint: RailCheckpoint) => string;
}

export interface CheckpointRailState {
  /** Every checkpoint in the project, laid out as lanes, oldest first. */
  graph: CheckpointGraph;
  /** Snapshot the whole project under `description`. */
  create: (description: string) => Promise<void>;
  /** Put the project back to this checkpoint. Files only. */
  restore: (checkpoint: RailCheckpoint) => Promise<void>;
  /** Drop this restore point. The files it captured are untouched. */
  remove: (checkpoint: RailCheckpoint) => Promise<void>;
  /** Id of the checkpoint currently restoring, for the rail's spinner. */
  restoring: string | null;
}

export function useCheckpointRail(
  opts: CheckpointRailOptions,
): CheckpointRailState {
  const { repo, indexUrl, checkpointsDocUrl, files, restoreMessage } = opts;
  const [refsDoc] = useDocument<CheckpointsRefDoc>(checkpointsDocUrl, {
    suspense: false,
  });
  const [restoring, setRestoring] = useState<string | null>(null);

  const refs = refsDoc?.checkpoints;
  const head = refsDoc?.head;
  const graph = useMemo(() => railGraph(refs, head), [refs, head]);

  const create = useCallback(
    async (description: string) => {
      invariant(repo, "Automerge repo is not available");
      invariant(checkpointsDocUrl, "checkpointsDocUrl not minted yet");
      await captureAndCreateCheckpoint(
        repo,
        checkpointsDocUrl,
        files,
        "/",
        description,
      );
    },
    [repo, checkpointsDocUrl, files],
  );

  const restore = useCallback(
    async (checkpoint: RailCheckpoint) => {
      invariant(repo, "Automerge repo is not available");
      invariant(checkpointsDocUrl, "checkpointsDocUrl not minted yet");
      const ref = (refs ?? []).find((c) => c.id === checkpoint.id);
      invariant(ref, `no checkpoint ${checkpoint.id}`);
      setRestoring(checkpoint.id);
      try {
        await revertToCheckpoint({
          repo,
          indexUrl,
          checkpointsDocUrl,
          checkpoint: ref,
          message: restoreMessage(checkpoint),
        });
      } finally {
        setRestoring(null);
      }
    },
    [repo, indexUrl, checkpointsDocUrl, refs, restoreMessage],
  );

  const remove = useCallback(
    async (checkpoint: RailCheckpoint) => {
      invariant(repo, "Automerge repo is not available");
      invariant(checkpointsDocUrl, "checkpointsDocUrl not minted yet");
      // Only the ref goes. Its content doc is left orphaned for GC to archive
      // and reclaim. Deleting it inline could race a concurrent restore.
      await deleteCheckpoint(repo, checkpointsDocUrl, checkpoint.id);
    },
    [repo, checkpointsDocUrl],
  );

  return { graph, create, restore, remove, restoring };
}
